/**
 * Imports
 * @internal
 * @ignore
 */
import { Box, IconButton, Paper } from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'
import Heading from 'components/heading'
import { InlineLoader } from 'components/loader'
import NoData from 'components/no-data'
import InfoTitle from 'components/page/reporting/info-title'
import { ReactElement } from 'react'
import { CSVLink } from 'react-csv'
import { ResponsiveContainer } from 'recharts'
import InfoDialog from './InfoDialog'

/**
 * Types
 * @internal
 * @ignore
 */
/** Chart Wrapper component props. */
interface ChartWrapperProps {
  /** No data state. */
  noData: boolean
  /** Loading state. */
  loading: boolean
  /** Chart title. */
  title?: string
  /** Chart subtitle. */
  subtitle?: string
  /** Extra information text or custom element to display in a popup. */
  info?: string | (() => ReactElement)
  /** Title for info popup. */
  infoTitle?: string
  /** Child Components, charts, custom elements etc. */
  children: ReactElement
  /** Should children be wrapped in a re-charts responsive container.  */
  responsive?: boolean
  /** Custom height override. */
  height?: number
  /** Csv data for react-csv to download. if provided, will render a button to download this data. */
  csv?: {
    data: Array<Record<string, any>>
    headers?: Array<Record<'label' | 'key', string>>
  }
  /** Css overrides for custom components. */
  css?: {
    subtitleHeight?: number
    contentHeight?: number
    px?: number
    p?: number
  }
}

/**
 * Generic Chart Wrapper for all charts. This is to unify/standardize charts
 * across the dashbaord, removing the need to manually set heights of
 * different types of charts components in order for them to allign correctly. Widths
 * should be defined using Grids/Flex and breakpoints.
 *
 * By default, the wrapper assumes your first child component is the chart
 * itself. If you require more control over the child components, set responsive
 * prop to false and wrap your chart within in a responsive container yourself (
 * you may need to manually configure heights this way).
 * TODO: add legend as well or figure out a way to display legend without
 * shifting hight.
 *
 * @example
 * ```tsx
 * <ChartWrapper responsive={false}>
 *  <div> <ResponsiveContainer> <Chart/> </ResponsiveContainer> </div>
 * </ChartWrapper>
 * ```
 * @param opts Component Options.
 * @param opts.noData No data state.
 * @param opts.loading Loading state.
 * @param opts.title Chart Title.
 * @param opts.csv Csv data for downloading.
 * @param opts.subtitle Chart sub-title text to render above the chart, if this is
 * more than a sentance or 2, rather render an info component to explain the chart.
 * @param opts.info Info text or component.
 * @param opts.infoTitle Info title text.
 * @param opts.children Children componenets.
 * @param opts.responsive Should child components be wrapped by re-charts responsive container.
 * @param opts.height Override wrapper height.
 * @param opts.css Override component css.
 * @returns ChartWrapper Component.
 */
const ChartWrapper = (
  { noData, loading, title, subtitle, info, infoTitle, children, responsive, height, csv, css }: ChartWrapperProps): ReactElement => {
  responsive = responsive ?? true
  height = height ?? 420
  const cssO = {
    subtitleHeight: css?.subtitleHeight ?? 60,
    px: css?.px ?? 3,
    p: css?.p ?? 2,
    contentHeight: css?.contentHeight ?? height - 100
  }
  return (
    <Box display="flex" flexDirection="column" height="100%">
      {title && <Heading>{title}</Heading>}
      <Paper>
        <Box p={cssO.p} display="flex" flexDirection="column" height={height} position="relative">
          {noData && !loading && <NoData />}
          {loading && !noData && <InlineLoader />}
          {!noData && !loading &&
            <Box display="flex" flexDirection="column">
              <Box height={`${cssO.subtitleHeight}px`} pl={1} display="flex" justifyContent="space-between" alignItems="start">
                <InfoTitle title={subtitle} />
                <Box display="flex">
                  {info && <InfoDialog title={infoTitle} infoComponent={info} />}
                  {csv &&
                    <CSVLink data={csv.data} headers={csv.headers} filename={title}>
                      <IconButton size="small">
                        <GetAppIcon />
                      </IconButton>
                    </CSVLink>
                  }
                </Box>
              </Box>
              {responsive
                ? <Box px={cssO.px}>
                  <ResponsiveContainer height={cssO.contentHeight}>
                    {children}
                  </ResponsiveContainer>
                </Box>
                : <Box px={cssO.px} height={cssO.contentHeight}>
                  {children}
                </Box>
              }
            </Box>
          }
        </Box>
      </Paper>
    </Box>
  )
}

export default ChartWrapper
